import React, { useRef, useLayoutEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { LandingHeader } from './LandingHeader';
import { LandingFooter } from './LandingFooter';

const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.25,
      ease: 'easeOut',
    },
  },
};

/**
 * Main layout wrapper for all landing pages
 * Includes the header, main content area (via Outlet), and footer
 * Uses overflow:auto to handle scrolling since root has overflow:hidden
 * Adds fade transitions between page navigations
 */
export const LandingLayout: React.FC = () => {
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll to top on route change
  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <Box
      ref={scrollRef}
      data-scroll-container="landing"
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        bgcolor: 'background.default',
      }}
    >
      <LandingHeader />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <motion.div
          key={location.pathname}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}
        >
          <Outlet />
        </motion.div>
      </Box>
      <LandingFooter />
    </Box>
  );
};
